import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ArrowLeft, Save, Upload, User, Car, CreditCard, FileText, Smartphone, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DatePickerController } from '@/components/ui/date-picker';
import { toast } from 'sonner';
import { useDriver, useUpdateDriver } from '@/api-services/hooks/useDriver';
import { DriverDTO } from '@/api-services/hooks/adminTypes';

const driverFormSchema = z.object({
  // Personal Information
  phoneNumber: z.string()
    .min(10, 'Phone number must be at least 10 digits')
    .max(20, 'Phone number must not exceed 20 digits')
    .regex(/^\+?[\d\s\-\(\)]+$/, 'Invalid phone number format. Use digits, spaces, hyphens, and parentheses only'),
  email: z.string().email('Invalid email address').optional().or(z.literal('')),
  fullName: z.string().min(2, 'Full name is required'),
  dateOfBirth: z.string().optional(),
  gender: z.string().optional(),
  address: z.string().optional(),
  emergencyContactNumber: z.string().optional(),
  preferredLanguage: z.string().optional(),
  // Accept URL or data URL (from file uploads)
  profilePhotoUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),

  // Government ID
  govtIdType: z.string().optional(),
  govtIdNumber: z.string().optional(),
  govtIdFrontUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),
  govtIdBackUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),
  drivingLicenseFrontUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),
  drivingLicenseBackUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),
  selfieWithIdUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),

  // Vehicle Information
  vehicleType: z.string().optional(),
  vehicleMake: z.string().optional(),
  vehicleModel: z.string().optional(),
  vehicleRegistrationNumber: z.string().optional(),
  yearOfManufacture: z.number().min(1900).max(new Date().getFullYear() + 1).optional().or(z.literal('')),
  vehicleColor: z.string().optional(),
  seatingCapacity: z.number().min(1).optional().or(z.literal('')),
  luggageCapacity: z.number().min(0).optional().or(z.literal('')),
  ownershipDocUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),
  insuranceDocUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),
  roadTaxDocUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),

  // Banking Information
  bankAccountHolderName: z.string().optional(),
  bankName: z.string().optional(),
  accountNumber: z.string().optional(),
  ifscCode: z.string().optional(),
  swiftCode: z.string().optional(),
  paymentFrequency: z.string().optional(),

  // Device & App Information
  deviceType: z.string().optional(),
  osVersion: z.string().optional(),
  fcmToken: z.string().optional(),
  appVersion: z.string().optional(),
  geolocationPermission: z.boolean().optional(),

  // Additional Fields
  signupMethod: z.string().optional(),
  otpVerified: z.boolean().optional(),
  termsConsent: z.boolean().optional(),
  ewalletId: z.string().optional(),
});

type DriverFormData = z.infer<typeof driverFormSchema>;

const EditDriverPage: React.FC = () => {
  const navigate = useNavigate();
  const { driverId } = useParams<{ driverId: string }>();
  const driverIdNum = driverId ? parseInt(driverId, 10) : 0;
  const [activeTab, setActiveTab] = useState('personal');
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, File>>({});

  // Fetch driver data
  const { data: driverData, loading: loadingDriver, error: driverError } = useDriver(driverIdNum, {
    onError: (error) => {
      toast.error(`Failed to load driver: ${error.message}`);
    },
  });

  const { mutate: updateDriver, isLoading } = useUpdateDriver({
    onSuccess: (data) => {
      toast.success('Driver updated successfully!');
      navigate('/admin/drivers');
    },
    onError: (error) => {
      toast.error(`Failed to update driver: ${error.message}`);
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<DriverFormData>({
    resolver: zodResolver(driverFormSchema),
    defaultValues: {
      otpVerified: false,
      geolocationPermission: false,
      termsConsent: false,
    },
  });

  // Populate form when driver data is loaded
  useEffect(() => {
    if (driverData) {
      reset({
        phoneNumber: driverData.phoneNumber || '',
        email: driverData.email || '',
        fullName: driverData.fullName || '',
        dateOfBirth: driverData.dateOfBirth || '',
        gender: driverData.gender || '',
        address: driverData.address || '',
        emergencyContactNumber: driverData.emergencyContactNumber || '',
        preferredLanguage: driverData.preferredLanguage || '',
        profilePhotoUrl: driverData.profilePhotoUrl || '',
        govtIdType: driverData.govtIdType || '',
        govtIdNumber: driverData.govtIdNumber || '',
        govtIdFrontUrl: driverData.govtIdFrontUrl || '',
        govtIdBackUrl: driverData.govtIdBackUrl || '',
        drivingLicenseFrontUrl: driverData.drivingLicenseFrontUrl || '',
        drivingLicenseBackUrl: driverData.drivingLicenseBackUrl || '',
        selfieWithIdUrl: driverData.selfieWithIdUrl || '',
        vehicleType: driverData.vehicleType || '',
        vehicleMake: driverData.vehicleMake || '',
        vehicleModel: driverData.vehicleModel || '',
        vehicleRegistrationNumber: driverData.vehicleRegistrationNumber || driverData.vehicleLicensePlate || '',
        yearOfManufacture: driverData.yearOfManufacture || driverData.vehicleYear || undefined,
        vehicleColor: driverData.vehicleColor || '',
        seatingCapacity: driverData.seatingCapacity || undefined,
        luggageCapacity: driverData.luggageCapacity || undefined,
        ownershipDocUrl: driverData.ownershipDocUrl || driverData.vehicleRegistrationCertUrl || '',
        insuranceDocUrl: driverData.insuranceDocUrl || driverData.vehicleInsuranceCertUrl || '',
        roadTaxDocUrl: driverData.roadTaxDocUrl || '',
        bankAccountHolderName: driverData.bankAccountHolderName || '',
        bankName: driverData.bankName || '',
        accountNumber: driverData.accountNumber || driverData.bankAccountNumber || '',
        ifscCode: driverData.ifscCode || driverData.bankIfscCode || '',
        swiftCode: driverData.swiftCode || '',
        paymentFrequency: driverData.paymentFrequency || '',
        deviceType: driverData.deviceType || '',
        osVersion: driverData.osVersion || '',
        fcmToken: driverData.fcmToken || '',
        appVersion: driverData.appVersion || '',
        geolocationPermission: driverData.geolocationPermission ?? false,
        signupMethod: driverData.signupMethod || '',
        otpVerified: driverData.otpVerified ?? false,
        termsConsent: driverData.termsConsent ?? false,
        ewalletId: driverData.ewalletId || '',
      });
    }
  }, [driverData]); // Removed 'reset' from dependencies to prevent infinite loop

  // Handle file upload - convert to base64 data URL
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldName: keyof DriverFormData
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Store the file for potential later upload
      setUploadedFiles((prev) => ({
        ...prev,
        [fieldName]: file,
      }));

      // Convert to data URL (base64) for immediate use
      // In production, you'd upload this file to your server first
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        setValue(fieldName as any, dataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  // Helper function to render file upload field with URL option
  const renderDocumentField = (
    fieldName: keyof DriverFormData,
    label: string,
    placeholder: string
  ) => {
    const hasFile = uploadedFiles[fieldName as string];
    const currentValue = watch(fieldName);

    return (
      <div className="space-y-2 md:col-span-2">
        <Label>{label}</Label>
        <div className="space-y-3">
          {/* File Upload Option */}
          <div className="flex items-center gap-3">
            <Input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => handleFileUpload(e, fieldName)}
              className="flex-1"
            />
            {hasFile && (
              <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                {hasFile.name}
              </span>
            )}
          </div>
          
          {/* OR Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or</span>
            </div>
          </div>

          {/* URL Input Option */}
          <Input
            {...register(fieldName)}
            placeholder={placeholder}
            value={currentValue || ''}
          />
          {errors[fieldName] && (
            <p className="text-sm text-destructive">{errors[fieldName]?.message}</p>
          )}
          {currentValue && (
            <p className="text-xs text-muted-foreground">
              {currentValue.startsWith('data:') ? 'File uploaded' : 'URL provided'}
            </p>
          )}
        </div>
      </div>
    );
  };

  const onSubmit = (data: DriverFormData) => {
    if (!driverIdNum) {
      toast.error('Driver ID is missing');
      return;
    }

    // Clean the data before sending to API
    const driverUpdateData: DriverDTO = {
      driverId: driverIdNum,
      phoneNumber: data.phoneNumber.trim(),
      email: data.email?.trim() || undefined,
      fullName: data.fullName.trim(),
      dateOfBirth: data.dateOfBirth || undefined,
      gender: data.gender || undefined,
      address: data.address?.trim() || undefined,
      emergencyContactNumber: data.emergencyContactNumber?.trim() || undefined,
      preferredLanguage: data.preferredLanguage || undefined,
      profilePhotoUrl: data.profilePhotoUrl?.trim() || undefined,
      govtIdType: data.govtIdType || undefined,
      govtIdNumber: data.govtIdNumber || undefined,
      govtIdFrontUrl: data.govtIdFrontUrl || undefined,
      govtIdBackUrl: data.govtIdBackUrl || undefined,
      drivingLicenseFrontUrl: data.drivingLicenseFrontUrl || undefined,
      drivingLicenseBackUrl: data.drivingLicenseBackUrl || undefined,
      selfieWithIdUrl: data.selfieWithIdUrl || undefined,
      vehicleType: data.vehicleType || undefined,
      vehicleMake: data.vehicleMake || undefined,
      vehicleModel: data.vehicleModel || undefined,
      vehicleRegistrationNumber: data.vehicleRegistrationNumber || undefined,
      yearOfManufacture: data.yearOfManufacture ? Number(data.yearOfManufacture) : undefined,
      vehicleColor: data.vehicleColor || undefined,
      seatingCapacity: data.seatingCapacity ? Number(data.seatingCapacity) : undefined,
      luggageCapacity: data.luggageCapacity ? Number(data.luggageCapacity) : undefined,
      ownershipDocUrl: data.ownershipDocUrl || undefined,
      insuranceDocUrl: data.insuranceDocUrl || undefined,
      roadTaxDocUrl: data.roadTaxDocUrl || undefined,
      bankAccountHolderName: data.bankAccountHolderName || undefined,
      bankName: data.bankName || undefined,
      accountNumber: data.accountNumber || undefined,
      ifscCode: data.ifscCode || undefined,
      swiftCode: data.swiftCode || undefined,
      paymentFrequency: data.paymentFrequency || undefined,
      deviceType: data.deviceType || undefined,
      osVersion: data.osVersion || undefined,
      fcmToken: data.fcmToken || undefined,
      appVersion: data.appVersion || undefined,
      geolocationPermission: data.geolocationPermission ?? false,
      signupMethod: data.signupMethod || undefined,
      otpVerified: data.otpVerified ?? false,
      termsConsent: data.termsConsent ?? false,
      ewalletId: data.ewalletId?.trim() || undefined,
      isDeletedValue: driverData?.isDeletedValue ?? false,
      signupStatus: driverData?.signupStatus || 'PENDING',
    };

    updateDriver({ id: driverIdNum, data: driverUpdateData });
  };

  if (loadingDriver) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading driver details...</p>
        </div>
      </div>
    );
  }

  if (driverError || !driverData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <p className="text-destructive mb-4">
              {driverError?.message || 'Driver not found'}
            </p>
            <Button onClick={() => navigate('/admin/drivers')}>Back to Drivers</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/drivers')}
            className="hover:bg-muted"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Edit Driver</h1>
            <p className="text-muted-foreground mt-1">Update driver information</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="personal">
              <User className="w-4 h-4 mr-2" />
              Personal
            </TabsTrigger>
            <TabsTrigger value="documents">
              <FileText className="w-4 h-4 mr-2" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="vehicle">
              <Car className="w-4 h-4 mr-2" />
              Vehicle
            </TabsTrigger>
            <TabsTrigger value="banking">
              <CreditCard className="w-4 h-4 mr-2" />
              Banking
            </TabsTrigger>
            <TabsTrigger value="additional">
              <Smartphone className="w-4 h-4 mr-2" />
              Additional
            </TabsTrigger>
          </TabsList>

          {/* Personal Information Tab */}
          <TabsContent value="personal" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>Update the driver's personal details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phoneNumber">Phone Number *</Label>
                    <Input
                      id="phoneNumber"
                      {...register('phoneNumber')}
                      placeholder="+1234567890"
                    />
                    {errors.phoneNumber && (
                      <p className="text-sm text-destructive">{errors.phoneNumber.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      {...register('email')}
                      placeholder="driver@example.com"
                    />
                    {errors.email && (
                      <p className="text-sm text-destructive">{errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      id="fullName"
                      {...register('fullName')}
                      placeholder="John Doe"
                    />
                    {errors.fullName && (
                      <p className="text-sm text-destructive">{errors.fullName.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <DatePickerController
                      control={control}
                      name="dateOfBirth"
                      label="Date of Birth"
                      placeholder="Select date of birth"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="gender">Gender</Label>
                    <Select value={watch('gender')} onValueChange={(value) => setValue('gender', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                        <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      {...register('address')}
                      placeholder="123 Main St, City, State, ZIP"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="emergencyContactNumber">Emergency Contact Number</Label>
                    <Input
                      id="emergencyContactNumber"
                      {...register('emergencyContactNumber')}
                      placeholder="+1234567890"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="preferredLanguage">Preferred Language</Label>
                    <Select value={watch('preferredLanguage')} onValueChange={(value) => setValue('preferredLanguage', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="es">Spanish</SelectItem>
                        <SelectItem value="fr">French</SelectItem>
                        <SelectItem value="de">German</SelectItem>
                        <SelectItem value="hi">Hindi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Profile Photo */}
                  {renderDocumentField(
                    'profilePhotoUrl',
                    'Profile Photo',
                    'https://example.com/photo.jpg'
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="signupMethod">Signup Method</Label>
                    <Select value={watch('signupMethod')} onValueChange={(value) => setValue('signupMethod', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select signup method" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="google">Google</SelectItem>
                        <SelectItem value="facebook">Facebook</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2 pt-8">
                    <Checkbox
                      id="otpVerified"
                      checked={watch('otpVerified')}
                      onCheckedChange={(checked) => setValue('otpVerified', checked as boolean)}
                    />
                    <Label htmlFor="otpVerified" className="cursor-pointer">
                      OTP Verified
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Government ID & Documents</CardTitle>
                <CardDescription>Update driver identification documents</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="govtIdType">Government ID Type</Label>
                    <Select value={watch('govtIdType')} onValueChange={(value) => setValue('govtIdType', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select ID type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="passport">Passport</SelectItem>
                        <SelectItem value="national_id">National ID</SelectItem>
                        <SelectItem value="driving_license">Driving License</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="govtIdNumber">Government ID Number</Label>
                    <Input
                      id="govtIdNumber"
                      {...register('govtIdNumber')}
                      placeholder="ID number"
                    />
                  </div>

                  {/* Government ID Front */}
                  {renderDocumentField(
                    'govtIdFrontUrl',
                    'Government ID Front',
                    'https://example.com/doc-front.jpg'
                  )}

                  {/* Government ID Back */}
                  {renderDocumentField(
                    'govtIdBackUrl',
                    'Government ID Back',
                    'https://example.com/doc-back.jpg'
                  )}

                  {/* Driving License Front */}
                  {renderDocumentField(
                    'drivingLicenseFrontUrl',
                    'Driving License Front',
                    'https://example.com/license-front.jpg'
                  )}

                  {/* Driving License Back */}
                  {renderDocumentField(
                    'drivingLicenseBackUrl',
                    'Driving License Back',
                    'https://example.com/license-back.jpg'
                  )}

                  {/* Selfie with ID */}
                  {renderDocumentField(
                    'selfieWithIdUrl',
                    'Selfie with ID',
                    'https://example.com/selfie.jpg'
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vehicle Tab */}
          <TabsContent value="vehicle" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Vehicle Information</CardTitle>
                <CardDescription>Update vehicle details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="vehicleType">Vehicle Type</Label>
                    <Select value={watch('vehicleType')} onValueChange={(value) => setValue('vehicleType', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select vehicle type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sedan">Sedan</SelectItem>
                        <SelectItem value="suv">SUV</SelectItem>
                        <SelectItem value="hatchback">Hatchback</SelectItem>
                        <SelectItem value="coupe">Coupe</SelectItem>
                        <SelectItem value="van">Van</SelectItem>
                        <SelectItem value="truck">Truck</SelectItem>
                        <SelectItem value="motorcycle">Motorcycle</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vehicleMake">Vehicle Make</Label>
                    <Input
                      id="vehicleMake"
                      {...register('vehicleMake')}
                      placeholder="Toyota"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vehicleModel">Vehicle Model</Label>
                    <Input
                      id="vehicleModel"
                      {...register('vehicleModel')}
                      placeholder="Camry"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vehicleRegistrationNumber">Registration Number</Label>
                    <Input
                      id="vehicleRegistrationNumber"
                      {...register('vehicleRegistrationNumber')}
                      placeholder="ABC-1234"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="yearOfManufacture">Year of Manufacture</Label>
                    <Input
                      id="yearOfManufacture"
                      type="number"
                      {...register('yearOfManufacture', { valueAsNumber: true })}
                      placeholder="2020"
                      min="1900"
                      max={new Date().getFullYear() + 1}
                    />
                    {errors.yearOfManufacture && (
                      <p className="text-sm text-destructive">{errors.yearOfManufacture.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vehicleColor">Vehicle Color</Label>
                    <Input
                      id="vehicleColor"
                      {...register('vehicleColor')}
                      placeholder="White"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="seatingCapacity">Seating Capacity</Label>
                    <Input
                      id="seatingCapacity"
                      type="number"
                      {...register('seatingCapacity', { valueAsNumber: true })}
                      placeholder="4"
                      min="1"
                    />
                    {errors.seatingCapacity && (
                      <p className="text-sm text-destructive">{errors.seatingCapacity.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="luggageCapacity">Luggage Capacity</Label>
                    <Input
                      id="luggageCapacity"
                      type="number"
                      {...register('luggageCapacity', { valueAsNumber: true })}
                      placeholder="2"
                      min="0"
                    />
                    {errors.luggageCapacity && (
                      <p className="text-sm text-destructive">{errors.luggageCapacity.message}</p>
                    )}
                  </div>

                  {/* Ownership Document */}
                  {renderDocumentField(
                    'ownershipDocUrl',
                    'Ownership Document',
                    'https://example.com/ownership.pdf'
                  )}

                  {/* Insurance Document */}
                  {renderDocumentField(
                    'insuranceDocUrl',
                    'Insurance Document',
                    'https://example.com/insurance.pdf'
                  )}

                  {/* Road Tax Document */}
                  {renderDocumentField(
                    'roadTaxDocUrl',
                    'Road Tax Document',
                    'https://example.com/roadtax.pdf'
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Banking Tab */}
          <TabsContent value="banking" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Banking Information</CardTitle>
                <CardDescription>Update banking details for payments</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="bankAccountHolderName">Account Holder Name</Label>
                    <Input
                      id="bankAccountHolderName"
                      {...register('bankAccountHolderName')}
                      placeholder="John Doe"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bankName">Bank Name</Label>
                    <Input
                      id="bankName"
                      {...register('bankName')}
                      placeholder="ABC Bank"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="accountNumber">Account Number</Label>
                    <Input
                      id="accountNumber"
                      {...register('accountNumber')}
                      placeholder="1234567890"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ifscCode">IFSC Code</Label>
                    <Input
                      id="ifscCode"
                      {...register('ifscCode')}
                      placeholder="ABCD0123456"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="swiftCode">SWIFT Code</Label>
                    <Input
                      id="swiftCode"
                      {...register('swiftCode')}
                      placeholder="SWIFTCODE123"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="paymentFrequency">Payment Frequency</Label>
                    <Select value={watch('paymentFrequency')} onValueChange={(value) => setValue('paymentFrequency', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Bi-weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Additional Information Tab */}
          <TabsContent value="additional" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Additional Information</CardTitle>
                <CardDescription>Update device and app information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="deviceType">Device Type</Label>
                    <Select value={watch('deviceType')} onValueChange={(value) => setValue('deviceType', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select device type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="android">Android</SelectItem>
                        <SelectItem value="ios">iOS</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="osVersion">OS Version</Label>
                    <Input
                      id="osVersion"
                      {...register('osVersion')}
                      placeholder="14.0"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="appVersion">App Version</Label>
                    <Input
                      id="appVersion"
                      {...register('appVersion')}
                      placeholder="1.0.0"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="fcmToken">FCM Token</Label>
                    <Input
                      id="fcmToken"
                      {...register('fcmToken')}
                      placeholder="FCM token"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ewalletId">E-Wallet ID</Label>
                    <Input
                      id="ewalletId"
                      {...register('ewalletId')}
                      placeholder="wallet_123456"
                    />
                  </div>

                  <div className="flex items-center space-x-2 pt-8">
                    <Checkbox
                      id="geolocationPermission"
                      checked={watch('geolocationPermission')}
                      onCheckedChange={(checked) => setValue('geolocationPermission', checked as boolean)}
                    />
                    <Label htmlFor="geolocationPermission" className="cursor-pointer">
                      Geolocation Permission Granted
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="termsConsent"
                      checked={watch('termsConsent')}
                      onCheckedChange={(checked) => setValue('termsConsent', checked as boolean)}
                    />
                    <Label htmlFor="termsConsent" className="cursor-pointer">
                      Terms & Conditions Consented
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-4 pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/drivers')}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-gradient-primary hover:opacity-90"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                Updating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Update Driver
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditDriverPage;

